import {useCallback} from 'react'
import {useLazyQuery, useMutation} from '@apollo/client'
import {
  GET_ADMIN_NOTIFICATIONS,
  GET_ADMIN_NOTIFICATION_UNREAD_COUNT,
  MARK_ADMIN_NOTIFICATIONS_READ,
  MARK_ALL_ADMIN_NOTIFICATIONS_READ,
} from '../queries/admin-notification.query'
import {
  AdminNotificationFilters,
  AdminNotificationListResponse,
  AdminNotificationType,
  AdminNotificationUnreadCountResponse,
  MarkAdminNotificationsReadResponse,
} from '../types/admin-notification.type'

const graphqlErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback
  const apolloErr = error as {
    message?: string
    graphQLErrors?: {message?: string}[]
    networkError?: {message?: string}
  }
  const fromGql = apolloErr.graphQLErrors?.map((e) => e.message).filter(Boolean).join('; ')
  if (fromGql) return fromGql
  if (apolloErr.networkError?.message) return apolloErr.networkError.message
  if (apolloErr.message) return apolloErr.message
  return fallback
}

export const useAdminNotifications = () => {
  const [fetchListQuery, {loading: listLoading}] = useLazyQuery(GET_ADMIN_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const [fetchUnreadCountQuery] = useLazyQuery(GET_ADMIN_NOTIFICATION_UNREAD_COUNT, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const [markReadMutation] = useMutation(MARK_ADMIN_NOTIFICATIONS_READ)
  const [markAllReadMutation] = useMutation(MARK_ALL_ADMIN_NOTIFICATIONS_READ)

  const fetchNotifications = useCallback(
    async (filters: AdminNotificationFilters = {}): Promise<AdminNotificationListResponse> => {
      const {data, error} = await fetchListQuery({
        variables: {
          page: filters.page ?? 1,
          pageSize: filters.pageSize ?? 20,
          types: filters.types,
          unreadOnly: filters.unreadOnly,
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
      })

      const payload = data?.getAdminNotificationsR as AdminNotificationListResponse | undefined
      if (!payload) {
        throw new Error(graphqlErrorMessage(error, 'Failed to load notifications'))
      }
      if (error) {
        // Partial data still usable — surface message but return payload
        console.warn('Admin notifications list GraphQL warning:', error.message)
      }
      return payload
    },
    [fetchListQuery]
  )

  const fetchUnreadCount = useCallback(async (): Promise<AdminNotificationUnreadCountResponse> => {
    const {data, error} = await fetchUnreadCountQuery()
    const payload = data?.getAdminNotificationUnreadCountR as
      | AdminNotificationUnreadCountResponse
      | undefined
    if (!payload) {
      throw new Error(graphqlErrorMessage(error, 'Failed to load unread count'))
    }
    return payload
  }, [fetchUnreadCountQuery])

  const markRead = useCallback(
    async (ids: string[]): Promise<MarkAdminNotificationsReadResponse | undefined> => {
      const response = await markReadMutation({variables: {ids}})
      return response.data?.markAdminNotificationsReadR as
        | MarkAdminNotificationsReadResponse
        | undefined
    },
    [markReadMutation]
  )

  const markAllRead = useCallback(
    async (
      types?: AdminNotificationType[]
    ): Promise<MarkAdminNotificationsReadResponse | undefined> => {
      const response = await markAllReadMutation({variables: {types}})
      return response.data?.markAllAdminNotificationsReadR as
        | MarkAdminNotificationsReadResponse
        | undefined
    },
    [markAllReadMutation]
  )

  return {
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
    listLoading,
  }
}

export default useAdminNotifications
